import { toast } from "@loot-os/ui/components/sonner";
import { useEffect, useEffectEvent } from "react";

export interface StandingNotice {
  /** Dedupe key: raising the same notice again updates the toast instead of stacking one. */
  id: string;
  message: string;
  /** A button on the toast, usually a navigation to where the condition is fixed. */
  action?: { label: string; onClick: () => void };
  /**
   * How long the toast stays up, in ms. Defaults to 8s, which suits a condition the reader can
   * act on and then forget. Pass `Number.POSITIVE_INFINITY` for one they cannot fix and must
   * keep in mind — a platform-wide halt — so arriving after it was raised still shows it. It is
   * still withdrawn the moment the condition clears.
   */
  duration?: number;
}

/**
 * A standing condition announced as a toast rather than a banner — "the
 * storefront is disabled", "opens are paused". Raised when `active` turns true
 * (the id absorbs StrictMode's double effect and every re-render while it
 * holds) and withdrawn the moment it stops, so a switch flipped back does not
 * leave a stale warning up. The banners these replace pushed the page down as
 * they came and went; a toast leaves the layout alone.
 *
 * Re-raised when the message changes while the condition holds, because the
 * condition can deepen without ever going false: a second platform switch goes
 * down and the one toast already up has to say so. Sonner matches on the id, so
 * that updates the toast in place rather than stacking a second one.
 */
export function useStandingNotice(active: boolean, notice: StandingNotice) {
  // The message is an argument rather than read off `notice` here, so it is a real dependency of
  // the effect below. Everything else stays inside the effect event: `action` is a fresh object
  // every render and would re-raise the toast on each one.
  const raise = useEffectEvent((message: string) => {
    const duration = notice.duration ?? 8000;
    toast.warning(message, {
      id: notice.id,
      action: notice.action,
      duration,
      // A toast that never times out has to be dismissible, and the Toaster sets no global
      // close button: without this the only way out is a swipe, which nobody discovers.
      // Dismissing is per-reader and per-tab; the condition itself is untouched, and a reload
      // or a refetch while it still holds raises it again.
      closeButton: !Number.isFinite(duration),
    });
  });
  useEffect(() => {
    if (active) raise(notice.message);
    else toast.dismiss(notice.id);
  }, [active, notice.id, notice.message]);
}
